import { useCallback, useEffect, useRef, useState } from 'react';

export interface PanelGeometry {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface UseDraggableOptions {
  initial?: Partial<PanelGeometry>;
  min?: { w: number; h: number };
  /**
   * Ref opcional para persistir a geometria entre aberturas: o painel reabre
   * exatamente onde o usuário o deixou.
   */
  geometryRef?: React.MutableRefObject<PanelGeometry | null>;
  enabled?: boolean;
}

const MIN_VISIBLE = 80; // px do cabeçalho que precisam continuar na tela

/**
 * Arrasto e redimensionamento de uma janela flutuante via Pointer Events.
 *
 * Escrito à mão em vez de usar `@dnd-kit` (que está instalado): dnd-kit é
 * feito para draggable↔droppable com sensores e contexto, entrega um delta em
 * vez de posição absoluta e não tem noção de resize. É o mesmo espírito de
 * `useHighlightDraw` (PdfAnnotator) e `useMaskDraw` (OcclusionEditor).
 */
export default function useDraggable({
  initial,
  min = { w: 320, h: 200 },
  geometryRef,
  enabled = true,
}: UseDraggableOptions = {}) {
  const [geometry, setGeometry] = useState<PanelGeometry>(() => {
    if (geometryRef?.current) return geometryRef.current;
    const w = initial?.w ?? 460;
    const h = initial?.h ?? 420;
    return {
      w,
      h,
      x: initial?.x ?? Math.max(16, window.innerWidth - w - 48),
      y: initial?.y ?? 96,
    };
  });

  const dragState = useRef<{
    mode: 'move' | 'resize';
    startX: number;
    startY: number;
    start: PanelGeometry;
  } | null>(null);

  const clamp = useCallback((g: PanelGeometry): PanelGeometry => {
    const maxX = window.innerWidth - MIN_VISIBLE;
    const maxY = window.innerHeight - MIN_VISIBLE;
    return {
      w: Math.max(min.w, Math.min(g.w, window.innerWidth)),
      h: Math.max(min.h, Math.min(g.h, window.innerHeight)),
      x: Math.min(Math.max(g.x, MIN_VISIBLE - g.w), maxX),
      y: Math.min(Math.max(g.y, 0), maxY),
    };
  }, [min.w, min.h]);

  const commit = useCallback(
    (next: PanelGeometry) => {
      const clamped = clamp(next);
      setGeometry(clamped);
      if (geometryRef) geometryRef.current = clamped;
    },
    [clamp, geometryRef]
  );

  const onPointerDown = useCallback(
    (mode: 'move' | 'resize') => (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled || event.button !== 0) return;

      // Um clique num controle dentro da alça (o X de fechar, por exemplo) não
      // inicia arrasto. Sem isto, o setPointerCapture abaixo redireciona o
      // pointerup para a alça e o browser dispara o click nela, não no botão —
      // o onClick do botão simplesmente nunca roda.
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('button, a, input, textarea, select, [data-no-drag]')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragState.current = {
        mode,
        startX: event.clientX,
        startY: event.clientY,
        start: geometry,
      };
    },
    [enabled, geometry]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const state = dragState.current;
      if (!state) return;
      const dx = event.clientX - state.startX;
      const dy = event.clientY - state.startY;

      if (state.mode === 'move') {
        commit({ ...state.start, x: state.start.x + dx, y: state.start.y + dy });
      } else {
        commit({ ...state.start, w: state.start.w + dx, h: state.start.h + dy });
      }
    },
    [commit]
  );

  const onPointerUp = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (!dragState.current) return;
    dragState.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  /**
   * Setas movem o painel. O `stopPropagation` é obrigatório: o carrossel
   * escuta ArrowLeft/ArrowRight no `window` para trocar de questão, e sem isso
   * arrastar pelo teclado navegaria as questões junto.
   */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!enabled) return;
      const step = event.shiftKey ? 2 : 12;
      const moves: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, -step],
        ArrowDown: [0, step],
      };
      const delta = moves[event.key];
      if (!delta) return;
      event.preventDefault();
      event.stopPropagation();
      commit({ ...geometry, x: geometry.x + delta[0], y: geometry.y + delta[1] });
    },
    [enabled, geometry, commit]
  );

  // Reencaixa na viewport quando a janela muda de tamanho.
  useEffect(() => {
    const onResize = () => commit(geometry);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [commit, geometry]);

  return {
    geometry,
    dragHandleProps: {
      onPointerDown: onPointerDown('move'),
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
      onKeyDown,
      tabIndex: 0,
      role: 'button' as const,
    },
    resizeHandleProps: {
      onPointerDown: onPointerDown('resize'),
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
  };
}
